import { isDestroyed, isDestroying } from '@ember/destroyable';
import { fn, get } from '@ember/helper';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didUpdate } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import {
  PrismPerfectScrollbar,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { Ota } from 'frontend-utils/models';
import HotelDropdownsCompsetSelection from 'lumen/components/hotel/dropdowns/compset-selection';
import HotelAndMarketDropdownsSelectionOtas from 'lumen/components/hotel-and-market/dropdowns/selection-otas';
import {
  RATE_INSIGHT_FILTER_TYPE_RATE_PARAM,
  RATE_PARAMS,
} from 'lumen/utils/constants/general-constants';
import { timeout } from 'lumen/utils/waiters';

const RATE_FILTERS_CONTAINER_CLASS = '.ed-rate-filters-container';

interface HotelOverviewDashboardFiltersRateFiltersSignature {
  Args: {
    otaId: unknown;
    rateParam: unknown;
    selectOta: unknown;
    selectRateType: unknown;
    setCompsetId: unknown;
  };
}

export default class HotelOverviewDashboardFiltersRateFilters extends Component<HotelOverviewDashboardFiltersRateFiltersSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  perfectScrollbarInstance = null;

  resizeHandlerFunction = null;

  // Use Ember-concurrency to handle debouncing of function called on resize
  onResizeTask = task({ drop: true }, async () => {
    if (isDestroyed(this) || isDestroying(this)) {
      return;
    }

    await timeout();

    if (this.perfectScrollbarInstance) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.perfectScrollbarInstance.update();
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSecondaryCompset() {
    return this.hotelAndMarketService.hasSecondaryCompset;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ratesTypeFilterOptions() {
    return [
      {
        label: this.intl.t('rates.bar_short'),
        icon: 'rates-best-flex',
        id: RATE_PARAMS.BEST_FLEX,
        type: RATE_INSIGHT_FILTER_TYPE_RATE_PARAM,
      },
      {
        label: this.intl.t('rates.lowest'),
        icon: 'rates-lowest',
        id: RATE_PARAMS.LOWEST,
        type: RATE_INSIGHT_FILTER_TYPE_RATE_PARAM,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedOTA() {
    // @ts-expect-error: Incorrect type
    return this.store.peekRecord<Ota>('ota', this.args.otaId);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRateType() {
    return this.ratesTypeFilterOptions.find(
      (rateTypeOption) => rateTypeOption.id === this.args.rateParam,
    );
  }

  willDestroy(): void {
    super.willDestroy();

    if (this.perfectScrollbarInstance) {
      this.perfectScrollbarInstance = null;
    }

    if (this.resizeHandlerFunction) {
      document
        .querySelector(RATE_FILTERS_CONTAINER_CLASS)
        ?.removeEventListener('resize', this.resizeHandlerFunction);
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  onPerfectScrollbarUpdate(perfectScrollbarContainerScrollbarInstance): void {
    // Be sure that PerfectScrollbar has been fully initialised in the parent component
    if (perfectScrollbarContainerScrollbarInstance) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.perfectScrollbarInstance =
        perfectScrollbarContainerScrollbarInstance;

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      this.resizeHandlerFunction = () => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.onResizeTask.perform();
      };

      // @ts-expect-error: Incorrect type
      document
        .querySelector(RATE_FILTERS_CONTAINER_CLASS)
        // @ts-expect-error: Incorrect type
        .addEventListener('resize', this.resizeHandlerFunction);

      // Trigger once initially
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.onResizeTask.perform();
    }
  }

  <template>
    {{! @glint-nocheck }}
    <PrismPerfectScrollbar
      @wheelPropagation={{false}}
      class="ed-rate-filters-container"
      as |perfectScrollbar|
    >
      <PrismSelectInput
        @onChange={{@selectRateType}}
        @options={{this.ratesTypeFilterOptions}}
        @unsafe_isClampedToOneLine={{true}}
        @value={{this.selectedRateType}}
        {{! did-update here is a bit vague, but we can't rely on the did-insert in this case as the PS won't be initialised yet }}
        {{didUpdate
          (fn this.onPerfectScrollbarUpdate perfectScrollbar)
          perfectScrollbar
        }}
      />

      <HotelAndMarketDropdownsSelectionOtas
        @includeAllChannels={{true}}
        @onlyAllowMatchedOtas={{true}}
        @selectOta={{@selectOta}}
        @selectedOTA={{this.selectedOTA}}
        @type="rates"
      />

      <div class="select-wrapper">
        <div class="no-filter-content">
          {{t
            "rates.nights"
            count=(get
              this.hotelAndMarketService.hotelOrMarket.defaultLosRates "0"
            )
          }}
        </div>
      </div>

      <div class="select-wrapper">
        <div class="no-filter-content">
          {{t
            "rates.persons_selection"
            count=this.hotelAndMarketService.hotelOrMarket.defaultPersons
          }}
        </div>
      </div>

      <div class="select-wrapper">
        <div class="no-filter-content">
          {{t "rates.roomtypes.all"}}
        </div>
      </div>

      <div class="select-wrapper">
        <div class="no-filter-content">
          {{t "rates.meal.any"}}
        </div>
      </div>

      {{#if this.hasSecondaryCompset}}
        <div class="flex items-center justify-center">
          <HotelDropdownsCompsetSelection @setCompsetId={{@setCompsetId}} />
        </div>
      {{/if}}
    </PrismPerfectScrollbar>
  </template>
}
